import {IPlayerChannel, IPlayerUIState, LastOperationTypes} from "../../interfaces/IPlayerUIState";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import api from "../../newApi/apiNet";
import {IEpgItem} from "../../interfaces/IEpgItem";
import DateHelper from "../../helpers/dateHelper.ts";
import {RootState} from "../../stores/store.ts";
import {AxiosHeaders} from "axios";

const initialPlayerState: IPlayerUIState = {
    isFullscreen: false,
    isMenuUIVisible: false,
    channelToChangeIndex: -1,
    actualChannelIndex: -1,
    isVolumeControlVisible: false,
    isQualityControlVisible: false,
    screenOrientation: "",
    actualEpgIndex: -1,
    lastActivity: new Date().getTime(),
    isBackgroundTaskRunning: false,
    channelsCount: -1,
    channels: [],
    isSmoothEpgScroll: true,
    lastOperationType: "NONE",
    nextEpgRequestLoading: false
};

export const playerUISlice = createSlice({
    name: "playerUI",
    initialState: initialPlayerState,
    reducers: {
        incrementChannelToChangeIndex: (state: IPlayerUIState) => {
            if (state.isMenuUIVisible && state.channelToChangeIndex < state.channels.length - 1) {
                state.channelToChangeIndex++;
                state.lastOperationType = "CHANGE_CHANNEL_DOWN";
                playerUISlice.caseReducers.setActualEpgIndexToActualTime(state);
            }
        },
        incrementActualEpgIndex: (state: IPlayerUIState) => {
            state.lastOperationType = "CHANGE_EPG_RIGHT";
            const channel = state.channels[state.channelToChangeIndex];
            if (channel && channel.epgList.length - 2 > state.actualEpgIndex)
                state.actualEpgIndex++;
        },
        decrementActualEpgIndex: (state: IPlayerUIState) => {
            state.lastOperationType = "CHANGE_EPG_LEFT";
            if (state.actualEpgIndex > 0)
                state.actualEpgIndex--;
        },
        decrementChannelToChangeIndex: (state: IPlayerUIState) => {
            if (state.isMenuUIVisible && state.channelToChangeIndex > 0) {
                state.channelToChangeIndex--;
                state.lastOperationType = "CHANGE_CHANNEL_UP"
                playerUISlice.caseReducers.setActualEpgIndexToActualTime(state);
            }
        },
        setActualChannelIndex: (
            state: IPlayerUIState,
            action: PayloadAction<number>
        ) => {
            state.actualChannelIndex = action.payload;
            state.channelToChangeIndex = action.payload;
        },
        setActualEpgIndex: (
            state: IPlayerUIState,
            action: PayloadAction<number>
        ) => {
            state.actualEpgIndex = action.payload;
        },
        setActualEpgIndexToActualTime: (
            state: IPlayerUIState
        ) => {
            if (state.channels.length > 0) {
                const channel = state.channels[state.channelToChangeIndex];
                if (channel) {
                    const actualEpgIndex = channel.epgList.findIndex(x => x.start_timestamp <= DateHelper.GetDateTimeNowAsUnixTimespan() && x.stop_timestamp > DateHelper.GetDateTimeNowAsUnixTimespan())
                    if (actualEpgIndex !== -1)
                        playerUISlice.caseReducers.setActualEpgIndex(state, {
                            payload: actualEpgIndex,
                            type: "setActualEpgIndex"
                        })
                }
            }
        },
        setChannelToChangeIndex: (
            state: IPlayerUIState,
            action: PayloadAction<number>
        ) => {
            state.channelToChangeIndex = action.payload;
        },
        setFirstEpgId: (state: IPlayerUIState, action: PayloadAction<{ chId: number, firstEpgId: number }>) => {
            const channel = state.channels.find(x => x.ch_id === action.payload.chId);
            if (channel)
                channel.firstEpgId = action.payload.firstEpgId;
        },
        setLastEpgId: (state: IPlayerUIState, action: PayloadAction<{ chId: number, lastEpgId: number }>) => {
            const channel = state.channels.find(x => x.ch_id === action.payload.chId);
            if (channel)
                channel.lastEpgId = action.payload.lastEpgId;
        },
        setIsFullscreen: (
            state: IPlayerUIState,
            action: PayloadAction<boolean>
        ) => {
            state.isFullscreen = action.payload;
        },
        setIsMenuVisible: (
            state: IPlayerUIState,
            action: PayloadAction<boolean>
        ) => {
            if (!state.isBackgroundTaskRunning) {
                if (action.payload) {
                    if (!state.isMenuUIVisible) state.isMenuUIVisible = action.payload;
                }
                if (!action.payload) {
                    state.isMenuUIVisible = action.payload;
                    if (state.channelToChangeIndex !== state.actualChannelIndex) {
                        state.channelToChangeIndex = state.actualChannelIndex;
                    }
                    playerUISlice.caseReducers.setActualEpgIndexToActualTime(state);
                }
            }
        },
        setLastOperationType: (state: IPlayerUIState, action: PayloadAction<LastOperationTypes>) => {
            if (state.lastOperationType !== action.payload)
                state.lastOperationType = action.payload;
        },
        setIsBackgroundTaskRunning: (state: IPlayerUIState, action: PayloadAction<boolean>) => {
            state.isBackgroundTaskRunning = action.payload;
        },
        toggleFullscreen: (state: IPlayerUIState) => {
            state.isFullscreen = !state.isFullscreen;
        },
        toggleMenuVisible: (state: IPlayerUIState) => {
            state.isMenuUIVisible = !state.isMenuUIVisible;
        },
        toggleVolumeControlVisible: (state: IPlayerUIState) => {
            state.isVolumeControlVisible = !state.isVolumeControlVisible;
        },
        toggleQualityControlVisible: (state: IPlayerUIState) => {
            state.isQualityControlVisible = !state.isQualityControlVisible;
        },
    },
    extraReducers(builder) {
        builder.addCase(getChannelsAsync.fulfilled, (state, action) => {
            if (state.channels.length === 0) state.channels.push(...action.payload.map(x => {
                return {...x, firstEpgId: 0, lastEpgId: 0} as IPlayerChannel
            }));
        });
        builder.addCase(getNextEpgItemsAsync.pending, (state) => {
            state.nextEpgRequestLoading = true;
        });
        builder.addCase(getNextEpgItemsAsync.fulfilled, (state, action) => {
            state.nextEpgRequestLoading = false;
            if (action.payload.length > 0) {
                const channel = state.channels.find(x => x.ch_id === action.payload[0].ch_id);
                if (channel) {
                    const epgList: IEpgItem[] = [];
                    action.payload.forEach(epgItem => {
                        if (!channel?.epgList.find(x => x.id === epgItem.id))
                            epgList.push(epgItem);
                    });
                    epgList.sort(x => x.start_timestamp);
                    state.channels.find(x => x.ch_id === action.payload[0].ch_id)?.epgList.push(...epgList);
                }
            }
        });
        builder.addCase(getNextEpgItemsAsync.rejected, (state) => {
            state.nextEpgRequestLoading = false;
        });
        builder.addCase(getPreviousEpgItemsAsync.fulfilled, (state, action) => {
            if (action.payload.length > 0) {
                const channel = state.channels.find(x => x.ch_id === action.payload[0].ch_id);
                if (channel) {
                    const epgList: IEpgItem[] = [];
                    action.payload.forEach(epgItem => {
                        if (!channel?.epgList.find(x => x.id === epgItem.id))
                            epgList.push(epgItem);
                    });
                    epgList.sort(x => x.stop_timestamp);
                    state.channels.find(x => x.ch_id === action.payload[0].ch_id)?.epgList.unshift(...epgList);
                    state.actualEpgIndex = state.actualEpgIndex + action.payload.length;
                    state.lastOperationType = "FETCHED_PREV_EPG";
                }
            }
        });
    },
});

export const decrementActualEpgIndex = createAsyncThunk<void, void, { state: RootState }>(
    "epgs/decrement-actual-epg-index",
    async (_, {getState, dispatch}) => {
        const actualEpgIndex = getState().playerUI.actualEpgIndex;
        const channelToChange = getState().playerUI.channelToChangeIndex;
        const channel = getState().playerUI.channels[channelToChange];
        if (channel.epgList.length > 0 && actualEpgIndex - 2 < 0) {
            const firstEpgItem = channel.epgList[0];
            if (channel.epgList[0].id !== channel.firstEpgId)
                dispatch(getPreviousEpgItemsAsync({
                    chId: channel.ch_id,
                    toUnixTimeStamp: firstEpgItem.start_timestamp,
                    limit: 10
                }));
        } else {
            dispatch(playerUIActions.decrementActualEpgIndex());
        }
    }
)

export const incrementActualEpgIndex = createAsyncThunk<void, void, { state: RootState }>(
    "epgs/increment-actual-epg-index",
    async (_, {getState, dispatch}) => {
        const actualEpgIndex = getState().playerUI.actualEpgIndex;
        const channelToChange = getState().playerUI.channelToChangeIndex;
        const channel = getState().playerUI.channels[channelToChange];
        if (channel.epgList.length > 0 && (channel.epgList.length - (actualEpgIndex + 3) < 0)) {
            const lastEpgItem = channel.epgList[channel.epgList.length - 1];
            if (lastEpgItem.id !== channel.lastEpgId)
                dispatch(getNextEpgItemsAsync({
                    chId: channel.ch_id,
                    fromUnixTimeStamp: lastEpgItem.stop_timestamp,
                    limit: 10
                }));
        } else {
            dispatch(playerUIActions.incrementActualEpgIndex());
        }
    }
)

export const getChannelsAsync = createAsyncThunk(
    "channel/vm-for-player",
    async () => {
        const result = await api.VmForPlayer();
        return result.data.filter((x) => x.enabled).sort((a, b) => a.position - b.position);
    }
);

export const getPreviousEpgItemsAsync = createAsyncThunk<IEpgItem[], {
    chId: number,
    toUnixTimeStamp: number,
    limit: number
}>(
    "epgs/previous-items",
    async ({chId, toUnixTimeStamp, limit}, {dispatch}) => {
        const result = await api.getPreviousEpgItems(chId, toUnixTimeStamp, limit);
        const headers = result.headers;
        if (headers instanceof AxiosHeaders && headers.has('x-first-element-id')) {
            const firstEpgId = Number(headers.get('x-first-element-id'));
            dispatch(playerUIActions.setFirstEpgId({chId, firstEpgId}));
        }
        return result.data;
    }
);

export const getNextEpgItemsAsync = createAsyncThunk<IEpgItem[], {
    chId: number,
    fromUnixTimeStamp: number,
    limit: number
}>(
    "epgs/next-items",
    async ({chId, fromUnixTimeStamp, limit}, {dispatch}) => {
        const result = await api.getNextEpgItems(chId, fromUnixTimeStamp, limit);
        const headers = result.headers;
        if (headers instanceof AxiosHeaders && headers.has('x-last-element-id')) {
            const lastEpgId = Number(headers.get('x-last-element-id'));
            dispatch(playerUIActions.setLastEpgId({chId, lastEpgId}));
        }
        return result.data;
    }
);

export const playerUIActions = playerUISlice.actions;
export default playerUISlice.reducer;